import { useToast } from '@/hooks'
import { BANKRUPTCY_SCREENING_STATUSES } from '@/modules/tools/bankruptcy/constants'
import {
  useFetchFilingDetail,
  useFetchFilings,
  useFilingDetail,
  useFilingDetailStatus,
  useFilingMutations,
  useFilingsMutating,
} from '@/modules/tools/bankruptcy/hooks'
import type { Filing } from '@/modules/tools/bankruptcy/stores/types'
import { CaseHeader } from './CaseHeader'
import { CaseReview } from './CaseReview'
import { CaseSkeleton } from './CaseSkeleton'
import { CaseUnavailable } from './CaseUnavailable'
import { DocumentsSection } from './DocumentsSection'
import { useScreeningOutcomeToast } from './hooks'
import { RemoveDocumentDialog } from './RemoveDocumentDialog'
import { ScreeningSection } from './ScreeningSection'
import { ScreeningSteps } from './ScreeningSteps'

const isScreening = (filing: Filing) =>
  BANKRUPTCY_SCREENING_STATUSES.includes(filing.status)

const currentStep = (filing: Filing) => {
  if (filing.numberOfPages === 0) return 1
  if (!filing.screenedAt && !isScreening(filing)) return 2
  return 3
}

export const CasePage = ({ filingId }: { filingId: number }) => {
  useFetchFilings()
  const filing = useFilingDetail(filingId)
  const detailStatus = useFilingDetailStatus(filingId)
  const mutating = useFilingsMutating()
  const { screenFiling } = useFilingMutations()
  const { successToast, errorToast } = useToast()

  useScreeningOutcomeToast(filing?.status)
  useFetchFilingDetail(filingId, filing?.status)

  if (!filing) {
    if (detailStatus === 'missing' || detailStatus === 'failed') {
      return <CaseUnavailable reason={detailStatus} />
    }
    return <CaseSkeleton />
  }

  const handleScreen = () => {
    screenFiling(filing.id)
      .then(() => successToast('Screening started'))
      .catch((error) => errorToast('Failed to screen the case', error))
  }

  const documents = filing.documents ?? []
  const reviewing = !!filing.screenedAt && !isScreening(filing)

  return (
    <div className="mx-auto max-w-3xl space-y-8">
      <CaseHeader filing={filing} />
      <ScreeningSteps currentStep={currentStep(filing)} />

      {reviewing ? (
        <CaseReview
          filing={filing}
          documents={documents}
          isBusy={mutating}
          onRescreen={handleScreen}
        />
      ) : (
        <>
          <DocumentsSection filing={filing} documents={documents} />
          <div className="border-t pt-8">
            <ScreeningSection
              filing={filing}
              isBusy={mutating}
              onScreen={handleScreen}
            />
          </div>
        </>
      )}

      <RemoveDocumentDialog />
    </div>
  )
}
